import { SolarPanelType, SolarPanelTypeData } from '../store/ebc-solar-store';

export type QueryPostcodeAutocompleteResponse =
    | {
          description: string;
          matched_substrings: {
              length: number;
              offset: number;
          }[];
          place_id: string;
          reference: string;
          structured_formatting: {
              main_text: string;
              main_text_matched_substrings: {
                  length: number;
                  offset: number;
              }[];
              secondary_text: string;
          };
          terms: {
              offset: number;
              value: string;
          }[];
          types: string[];
      }[]
    | null;

export type QueryGeocodeResponse = {
    address_components: {
        long_name: string;
        short_name: string;
        types: string[];
    }[];
    formatted_address: string;
    geometry: {
        bounds: {
            northeast: {
                lat: number;
                lng: number;
            };
            southwest: {
                lat: number;
                lng: number;
            };
        };
        location: {
            lat: number;
            lng: number;
        };
        location_type: string;
        viewport: {
            northeast: {
                lat: number;
                lng: number;
            };
            southwest: {
                lat: number;
                lng: number;
            };
        };
    };
    place_id: string;
    types: string[];
}[];

export type QueryReverseGeocodeResponse =
    | {
          address_components: {
              long_name: string;
              short_name: string;
              types: string[];
          }[];
          formatted_address: string;
          geometry: {
              location: {
                  lat: number;
                  lng: number;
              };
              location_type: string;
              viewport: {
                  northeast: {
                      lat: number;
                      lng: number;
                  };
                  southwest: {
                      lat: number;
                      lng: number;
                  };
              };
          };
          place_id: string;
          plus_code: {
              compound_code: string;
              global_code: string;
          };
          types: string[];
      }[]
    | null;

export type QuerySolarBuildingInsightsResponse = {
    name: string;
    center: {
        latitude: number;
        longitude: number;
    };
    imageryDate: {
        year: number;
        month: number;
        day: number;
    };
    regionCode: string;
    solarPotential: {
        maxArrayPanelsCount: number;
        maxArrayAreaMeters2: number;
        maxSunshineHoursPerYear: number;
        carbonOffsetFactorKgPerMwh: number;
        wholeRoofStats: {
            areaMeters2: number;
            sunshineQuantiles: number[];
            groundAreaMeters2: number;
        };
        roofSegmentStats: {
            pitchDegrees: number;
            azimuthDegrees: number;
            stats: {
                areaMeters2: number;
                sunshineQuantiles: number[];
                groundAreaMeters2: number;
            };
            center: {
                latitude: number;
                longitude: number;
            };
            boundingBox: {
                sw: {
                    latitude: number;
                    longitude: number;
                };
                ne: {
                    latitude: number;
                    longitude: number;
                };
            };
            planeHeightAtCenterMeters: number;
        }[];
        solarPanelConfigs: {
            panelsCount: number;
            yearlyEnergyDcKwh: number;
            roofSegmentSummaries: {
                pitchDegrees: number;
                azimuthDegrees: number;
                panelsCount: number;
                yearlyEnergyDcKwh: number;
                segmentIndex: number;
            }[];
        }[];
        panelCapacityWatts: number;
        panelHeightMeters: number;
        panelWidthMeters: number;
        panelLifetimeYears: number;
        buildingStats: {
            areaMeters2: number;
            sunshineQuantiles: number[];
            groundAreaMeters2: number;
        };
        solarPanels: {
            center: {
                latitude: number;
                longitude: number;
            };
            orientation: 'PORTRAIT' | 'LANDSCAPE';
            yearlyEnergyDcKwh: number;
            segmentIndex: number;
        }[];
    };
    boundingBox: {
        sw: {
            latitude: number;
            longitude: number;
        };
        ne: {
            latitude: number;
            longitude: number;
        };
    };
    imageryQuality: 'HIGH' | 'MEDIUM' | 'LOW';
    imageryProcessedDate: {
        year: number;
        month: number;
        day: number;
    };
} | null;

class EbcSolarApi {
    static async queryPostcodeAutocomplete(postcode: string) {
        const url = 'https://europe-west2-ebc-software.cloudfunctions.net/ebc-solar-gmaps-autocomplete';

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ postcode }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            return data as QueryPostcodeAutocompleteResponse;
        } catch (e) {
            return null;
        }
    }

    static async queryGeocodeData(address: string) {
        const url = 'https://europe-west2-ebc-software.cloudfunctions.net/ebc-solar-gmaps-geocode';

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ address }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            return data as QueryGeocodeResponse;
        } catch (e) {
            return null;
        }
    }

    static async queryReverseGeocodeData(latitude: number, longitude: number) {
        const url = 'https://europe-west2-ebc-software.cloudfunctions.net/ebc-solar-gmaps-reverse-geocode';

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ latitude, longitude }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();
            // console.log(data);

            return data as QueryReverseGeocodeResponse;
        } catch (e) {
            return null;
        }
    }

    static async querySolarBuildingInsights(latitude: number, longitude: number) {
        const url = 'https://europe-west2-ebc-software.cloudfunctions.net/ebc-solar-gsolar-building-insights';

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ latitude, longitude }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            return data as QuerySolarBuildingInsightsResponse;
        } catch (e) {
            return null;
        }
    }

    static async submitSolarQuoteForm(
        fullName: string,
        phoneNumber: string,
        emailAddress: string,
        regionId: string,
        address: string,
        priceEstimate: number,
        solarPanelType: SolarPanelType,
        solarPanelAmount: number,
        batterySelected: boolean
    ) {
        /*
        Endpoint Fields
        name: string
        phone: string
        email: string
        address: string
        region_id: string
        quote_items: string[]

        */
        const url = 'https://europe-west2-ebc-software.cloudfunctions.net/ebc-submit-solar-quote-form';

        const solarPanelData = SolarPanelTypeData[solarPanelType];
        const quoteItems = [
            `Shown Price Estimate: £${priceEstimate}`,
            `Solar Panel Type: ${solarPanelData.name}, ${solarPanelData.wattage}W`,
            `Solar Panel Amount: X${solarPanelAmount}`,
            `Battery Selected: ${batterySelected === true ? 'Yes' : 'No'}`,
        ]

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({
                    name: fullName,
                    phone: phoneNumber,
                    email: emailAddress,
                    address: address,
                    region_id: regionId,
                    quote_items: quoteItems,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if(response.status !== 200) {
                return null;
            }

            return true;
        } catch (e) {
            return null;
        }
    }
}

export default EbcSolarApi;
